// src/pages/MuseMarket.js
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../auth/contexts/AuthContext';
import { PACKS } from '../../../data/packs';
import { Pack, TagDefinition } from '../../../types';
import RarityGem from '../../../shared/ui/RarityGem';
import { playSound } from '../../../utils/audio';
import '../styles/MuseMarket.css';

interface PackPurchaseModal {
  packId: string;
  pack: Pack;
}

const MuseMarket: React.FC = () => {
  const { userData, currentUser, updateUserData } = useAuth();
  const [loading, setLoading] = useState<boolean>(true);
  const [modal, setModal] = useState<PackPurchaseModal | null>(null);
  const [hoveredPack, setHoveredPack] = useState<string | null>(null);
  const [buying, setBuying] = useState<boolean>(false);
  const [tagDefinitions, setTagDefinitions] = useState<TagDefinition[]>([]);
  const [buyingTagId, setBuyingTagId] = useState<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (userData || currentUser === null) {
      setLoading(false);
    }
  }, [userData, currentUser]);

  // Load tagDefinitions for tag packs in the Muse Vault
  useEffect(() => {
    let isMounted = true;
    (async () => {
      try {
        const { collection, getDocs } = await import('firebase/firestore');
        const { db } = await import('../../../shared/storage/firebase');
        const snap = await getDocs(collection(db, 'tagDefinitions'));
        if (!isMounted) return;
        const defs = snap.docs.map(d => ({ id: d.id, ...(d.data() as any) })) as TagDefinition[];
        setTagDefinitions(defs);
      } catch (e) {
        console.error('Failed to load tag definitions:', e);
      }
    })();
    return () => { isMounted = false; };
  }, []);

  // Ticking clock for countdown
  const [nowTick, setNowTick] = useState<number>(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNowTick(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  const openModal = (packId: string, pack: Pack): void => {
    setModal({ packId, pack });
  };

  const renderComingSoonCard = (idx: number) => {
    const mixPct = 20 + (idx * 12); // vary navy-to-silver mix across the cards
    const headerGradient = `linear-gradient(135deg, #0b1a3a 0%, #0b1a3a ${Math.min(mixPct, 90)}%, #cfd9df 100%)`;
    return (
      <div
        key={`coming-soon-${idx}`}
        className={'market-card coming-soon'}
        style={{
          opacity: 0.95,
          border: '2px solid #cfd3db',
          boxShadow:
            'inset 0 1px 0 rgba(255,255,255,0.65), inset 0 -1px 0 rgba(0,0,0,0.15), 0 6px 14px rgba(0,0,0,0.18)',
        }}
      >
        <div
          className="market-card-header"
          style={{
            background: headerGradient,
            color: '#ffffff',
            textShadow: '0 1px 2px rgba(0,0,0,0.35)',
          }}
        >
          Mystery Muses
          <span className="new-banner">SOON</span>
        </div>
        <div className="market-card-body">
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              margin: '1rem 0 1.5rem 0',
              height: 120,
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 180,
                height: 120,
                borderRadius: 12,
                background: 'linear-gradient(135deg, #0b1a3a, #102a5c)',
                color: 'white',
                fontWeight: 900,
                fontSize: 64,
                textShadow: '0 3px 6px rgba(0,0,0,0.4)',
              }}
            >
              ?
            </div>
          </div>
          <button
            className="market-button market-button-secondary"
            disabled
            style={{ marginTop: 24 }}
          >
            Coming Soon
          </button>
        </div>
      </div>
    );
  };

  const closeModal = (): void => {
    setModal(null);
    setBuying(false);
  };

  const buyPack = async (): Promise<void> => {
    if (!modal || !userData || !currentUser) return;
    
    setBuying(true);
    try {
      const pack = PACKS[modal.packId];
      if (userData.museCash === undefined || userData.museCash < pack.price) {
        closeModal();
        return;
      }

      // Use a transaction to atomically decrement muse cash with validation
      const { doc, runTransaction, increment } = await import('firebase/firestore');
      const { db } = await import('../../../shared/storage/firebase');
      
      const userRef = doc(db, 'users', userData.id);
      const newBalance = await runTransaction(db, async (tx) => {
        const snap = await tx.get(userRef);
        if (!snap.exists()) throw new Error('User not found');
        const current = (snap.data().museCash || 0) as number;
        if (current < pack.price) throw new Error('Insufficient funds');
        // Prefer increment to avoid clobbering concurrent writers
        tx.update(userRef, { museCash: increment(-pack.price) });
        return current - pack.price;
      });

      // Optimistically update local state for instant UI feedback
      updateUserData({ museCash: newBalance });
      localStorage.setItem('openPack', modal.packId);

      // Reward-only SFX for successful purchase (before navigating to opening)
      try {
        playSound('/audio/badge-drop.mp3', 0.5);
      } catch (e) {
        console.warn('Failed to play pack purchase sound', e);
      }

      navigate('/app/pack');
    } catch (error) {
      console.error('Pack purchase error:', error);
      void window.appAlert('Failed to purchase pack. Please try again.');
    } finally {
      closeModal();
    }
  };

  const now = new Date(nowTick);
  const entries = Object.entries(PACKS);
  const seasonalPacks = entries.filter(([, p]) => p.seasonalGroup === 'Winter 2026');
  const nonSeasonalPacks = entries.filter(([, p]) => !p.seasonalGroup);

  const availableTagDefinitions = useMemo(() => {
    if (!userData || userData.role === 'parent') return [];
    const owned = new Set(userData.ownedTagIds || []);
    const userOgLevel = userData.ogLevel || 0;
    return tagDefinitions
      .filter(t => t.isActive !== false)
      .filter(t => !owned.has(t.id))
      .filter(t => {
        if (t.unlockType === 'level' && typeof t.requiredOgLevel === 'number') {
          return userOgLevel >= t.requiredOgLevel;
        }
        if (t.unlockType === 'season') {
          // Only show Blizzard Beats seasonal tag in the current Winter packs
          return t.seasonKey === 'blizzard-beats';
        }
        return true;
      })
      .sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));
  }, [tagDefinitions, userData]);

  const blizzardSeasonTag = useMemo(
    () => availableTagDefinitions.find(t => t.unlockType === 'season' && t.seasonKey === 'blizzard-beats') || null,
    [availableTagDefinitions]
  );

  const tagDefinitionsForTagSection = useMemo(
    () => availableTagDefinitions.filter(t => t.id !== blizzardSeasonTag?.id),
    [availableTagDefinitions, blizzardSeasonTag]
  );

  const getTimeLeft = (to: Date) => {
    const diff = +to - +now;
    if (diff <= 0) return '0 days';
    const dayMs = 24 * 60 * 60 * 1000;
    const days = Math.floor(diff / dayMs);
    return `${days} days`;
  };

  const renderPackCard = (id: string, pack: Pack) => {
    const packClass = `pack-${id.toLowerCase().replace(/\s+/g, '-')}`;
    const seasonalOutlineClass =
      pack.seasonalGroup === 'Fall 2025'
        ? 'fall-outline'
        : pack.seasonalGroup === 'Winter 2026'
        ? 'winter-outline'
        : '';
    const releaseAt = pack.releaseAt ? new Date(pack.releaseAt) : null;
    const endAt = pack.endAt ? new Date(pack.endAt) : null;
    const beforeRelease = releaseAt ? now < releaseAt : false;
    const afterEnd = endAt ? now > endAt : false;
    const showCountdown = !!(releaseAt || endAt);
    const countdownTo = beforeRelease ? releaseAt : (!afterEnd && endAt ? endAt : null);

    return (
      <div
        key={id}
        className={`market-card ${packClass} ${seasonalOutlineClass}`}
        onMouseEnter={() => setHoveredPack(id)}
        onMouseLeave={() => setHoveredPack(null)}
      >
        <div className="market-card-header">
          {pack.name}
          {pack.seasonalGroup && (
            <span className="new-banner">NEW</span>
          )}
        </div>
        <div className="market-card-body">
          <div>
            {/* Pack visual: overlap 3 avatars from the pack */}
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', margin: '1rem 0 1.5rem 0', height: 120, position: 'relative', zIndex: 1 }}>
              <div style={{ position: 'relative', width: 180, height: 120 }}>
                <img src={pack.muses[0].img} alt={pack.muses[0].name} style={{ position: 'absolute', left: 0, top: 0, width: 96, height: 96, objectFit: 'cover', zIndex: 3, filter: 'drop-shadow(0 4px 8px rgba(0, 0, 0, 0.3)) drop-shadow(0 2px 4px rgba(0, 0, 0, 0.2)) drop-shadow(0 1px 2px rgba(0, 0, 0, 0.1))', borderRadius: '10px' }} />
                {pack.muses[1] && <img src={pack.muses[1].img} alt={pack.muses[1].name} style={{ position: 'absolute', left: 48, top: 16, width: 96, height: 96, objectFit: 'cover', zIndex: 2, filter: 'drop-shadow(0 4px 8px rgba(0, 0, 0, 0.3)) drop-shadow(0 2px 4px rgba(0, 0, 0, 0.2)) drop-shadow(0 1px 2px rgba(0, 0, 0, 0.1))', borderRadius: '10px' }} />}
                {pack.muses[2] && <img src={pack.muses[2].img} alt={pack.muses[2].name} style={{ position: 'absolute', left: 96, top: 0, width: 96, height: 96, objectFit: 'cover', zIndex: 1, filter: 'drop-shadow(0 4px 8px rgba(0, 0, 0, 0.3)) drop-shadow(0 2px 4px rgba(0, 0, 0, 0.2)) drop-shadow(0 1px 2px rgba(0, 0, 0, 0.1))', borderRadius: '10px' }} />}
              </div>
            </div>
            {/* Removed pack description per request */}
          </div>

          {showCountdown && countdownTo && (
            <div className="countdown-chip">
              {beforeRelease ? 'Unlocks in ' : (afterEnd ? 'Season Ended' : 'Ends in ')}
              {!afterEnd && ` ${getTimeLeft(countdownTo)}`}
            </div>
          )}

          <button
            onClick={() => openModal(id, pack)}
            className={`market-button ${beforeRelease || afterEnd ? 'market-button-secondary' : (pack.seasonalGroup ? 'market-button-gold' : 'market-button-primary')}`}
            disabled={beforeRelease || afterEnd}
            style={{ 
              marginTop: 24,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: 'var(--spacing-md)'
            }}
          >
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.6rem', fontWeight: 800 }}>
              {beforeRelease ? 'Locked' : afterEnd ? 'Unavailable' : 'Get Pack'}
              {!beforeRelease && !afterEnd && (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.25rem' }}>
                  <span role="img" aria-label="token">💰</span>
                  {pack.price}
                </span>
              )}
            </span>
          </button>
          {/* Tooltip for drop rates */}
          <div className={`market-tooltip ${hoveredPack === id ? 'is-visible' : ''}`}>
            <div className="market-tooltip-title">Drop Rates</div>
            {pack.muses.map((m, i) => (
              <div key={i} className="market-tooltip-row">
                <img 
                  src={m.img} 
                  alt={m.name} 
                  style={{ 
                    width: 32, 
                    height: 32, 
                    objectFit: 'cover',
                    filter: 'drop-shadow(0 3px 6px rgba(0, 0, 0, 0.3)) drop-shadow(0 1px 3px rgba(0, 0, 0, 0.2))',
                    borderRadius: '4px'
                  }} 
                />
                <span className="muse-name">{m.name}</span>
                <RarityGem rarity={m.rarity} size={16} />
                <span className="muse-percentage">{m.weight}%</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  const renderTagCard = (tag: TagDefinition) => {
    if (!userData) return null;

    const userBalance = userData.museCash || 0;
    const userOgLevel = userData.ogLevel || 0;
    const hasRequiredLevel =
      tag.unlockType !== 'level' ||
      typeof tag.requiredOgLevel !== 'number' ||
      userOgLevel >= tag.requiredOgLevel;
    const canAfford = userBalance >= tag.priceMuseCash;
    const disabled = !hasRequiredLevel || !canAfford || buyingTagId === tag.id;

    // Determine primary button label
    let buttonLabel = 'Unlock Tag';
    if (!hasRequiredLevel && typeof tag.requiredOgLevel === 'number') {
      buttonLabel = `Reach OG ${tag.requiredOgLevel}`;
    } else if (buyingTagId === tag.id) {
      buttonLabel = 'Unlocking...';
    } else if (!canAfford) {
      buttonLabel = 'Not Enough';
    }

    return (
      <div
        key={tag.id}
        className="market-card tag-card"
      >
        <div className="market-card-header">
          {tag.label}
          {tag.unlockType === 'season' && (
            <span className="new-banner">SEASON</span>
          )}
        </div>
        <div className="market-card-body">
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', margin: '1rem 0 1.5rem 0', height: 120 }}>
            <div className={`tag-card-name tag-style-${tag.styleKey}`}>
              {tag.name}
            </div>
          </div>
          <button
            className={`market-button ${disabled ? 'market-button-secondary' : 'market-button-primary'}`}
            disabled={disabled}
            onClick={() => !disabled && handleBuyTag(tag)}
          >
            {!hasRequiredLevel || buyingTagId === tag.id ? (
              buttonLabel
            ) : (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.6rem', fontWeight: 800 }}>
                {buttonLabel}
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.25rem' }}>
                  <span role="img" aria-label="token">💰</span>
                  {tag.priceMuseCash}
                </span>
              </span>
            )}
          </button>
        </div>
      </div>
    );
  };

  const handleBuyTag = async (tag: TagDefinition) => {
    if (!userData) return;
    setBuyingTagId(tag.id);
    try {
      const { doc, runTransaction, increment, arrayUnion } = await import('firebase/firestore');
      const { db } = await import('../../../shared/storage/firebase');
      const userRef = doc(db, 'users', userData.id);

      const newBalance = await runTransaction(db, async (tx) => {
        const snap = await tx.get(userRef);
        if (!snap.exists()) throw new Error('User not found');
        const data = snap.data() as any;
        const currentBalance = data.museCash || 0;
        const ogLevel = data.ogLevel || 0;
        const owned: string[] = Array.isArray(data.ownedTagIds) ? data.ownedTagIds : [];

        if (owned.includes(tag.id)) {
          throw new Error('Tag already owned');
        }
        if (tag.unlockType === 'level' && typeof tag.requiredOgLevel === 'number' && ogLevel < tag.requiredOgLevel) {
          throw new Error('Insufficient level for this tag');
        }
        if (currentBalance < tag.priceMuseCash) {
          throw new Error('Insufficient funds');
        }

        tx.update(userRef, {
          museCash: increment(-tag.priceMuseCash),
          ownedTagIds: arrayUnion(tag.id),
        });

        return currentBalance - tag.priceMuseCash;
      });

      // Optimistically update local state
      const prevOwned = userData.ownedTagIds || [];
      updateUserData({
        museCash: newBalance,
        ownedTagIds: [...prevOwned, tag.id],
      });

      // Reward-only SFX for successfully unlocking a tag (big, triumphant moment)
      try {
        playSound('/audio/triumph.mp3', 0.7);
      } catch (e) {
        console.warn('Failed to play tag unlock sound', e);
      }
    } catch (error) {
      console.error('Tag purchase error:', error);
      void window.appAlert('Failed to unlock tag. Please try again.');
    } finally {
      setBuyingTagId(null);
    }
  };

  if (loading) {
    return (
      <div className="market-loading">
        <div className="market-loading-spinner">🔄</div>
        <p>Loading Muse Vault...</p>
      </div>
    );
  }

  return (
    <div className="market-container">
      <div className="market-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', marginBottom: '2.5rem' }}>
        <h1 style={{
          fontFamily: 'Fredoka One, Arial Rounded MT Bold, Arial, sans-serif',
          fontWeight: 900,
          fontSize: '3.5rem',
          color: '#111',
          margin: 0,
          letterSpacing: 2,
        }}>Muse Vault</h1>
      </div>
      {/* Seasonal section at top */}
      {seasonalPacks.length > 0 && (
        <>
          <div className="seasonal-section-title" style={{ color: '#0b1a3a' }}>
            NEW Limited Blizzard Beats Winter Muses
          </div>
          <div className="market-grid">
            {seasonalPacks.map(([id, pack]) => renderPackCard(id, pack))}
            {[0, 1, 2, 3].map(i => renderComingSoonCard(i))}
            {blizzardSeasonTag && renderTagCard(blizzardSeasonTag)}
          </div>
        </>
      )}

      {/* Tag Packs section */}
      {tagDefinitionsForTagSection.length > 0 && (
        <>
          <div className="tag-section-title">
            Tags
          </div>
          <div className="market-grid">
            {tagDefinitionsForTagSection.map(tag => renderTagCard(tag))}
          </div>
        </>
      )}

      {/* All Packs label and remaining packs */}
      <div className="all-packs-label">
        All Packs
      </div>
      <div className="market-grid">
        {nonSeasonalPacks.map(([id, pack]) => renderPackCard(id, pack))}
      </div>
      {/* Modal for pack purchase */}
      {modal && (
        <div className="market-modal-overlay" onClick={closeModal}>
          <div className="market-modal" onClick={e => e.stopPropagation()}>
            <div className="market-modal-title">
              Purchase <b>{modal.pack.name}</b> for <span role="img" aria-label="token">💰</span> {modal.pack.price}?
            </div>
            <div className="market-modal-actions">
              {userData && userData.museCash !== undefined && userData.museCash >= modal.pack.price ? (
                <button className="market-button market-button-primary" onClick={buyPack} disabled={buying}>
                  {buying ? 'Processing...' : `Buy for ${modal.pack.price} 💰`}
                </button>
              ) : (
                <button className="market-button market-button-secondary" onClick={closeModal}>
                  Not Enough Tokens
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MuseMarket;
